import {CommonModule} from '@angular/common';
import {Component} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {
  faArrowCircleLeft,
  faFileUpload,
  faPaperPlane,
  faPen,
  faSquareXmark,
  faTrash,
  faUpload
} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';
import {ApiService} from '../../services/api.service';
import {AuthService} from '../../services/auth.service';

type SubscriptionItem = {
  id?: number;
  name: string;
  hsn_sac?: string;
  quantity: number;
  unit_price: string;
  tax: string;
  is_recurring: boolean;
  is_one_time: boolean;
  year_applicable?: number | null;
};

type SubscriptionPayment = {
  id: number;
  year: number;
  amount: string;
  due_date: string;
  is_paid: boolean;
  paid_on?: string | null;
  saving?: boolean;
};

type SubscriptionDocument = {
  id: number;
  doc_type: string;
  file?: string;
  file_url?: string;
  original_filename?: string;
  uploaded_by_full_name?: string;
  uploaded_by_email?: string;
  created_at?: string;
};

type SubscriptionDetail = {
  id: number;
  organization_name: string;
  customer_poc_name?: string;
  customer_poc_email?: string;
  customer_poc_phone?: string;
  kyra_sales_poc_name?: string;
  kyra_sales_poc_email?: string;
  kyra_sales_poc_phone?: string;
  billing_type: string;
  status: string;
  start_date: string;
  next_payment_due_date?: string | null;
  expiry_date: string;
  renewal_date: string;
  contract_duration_years: number;
  total_amount: string;
  total_paid_amount?: string;
  pending_amount?: string;
  currency: string;
  camera_count?: number | null;
  cameras?: number | null;
  items: SubscriptionItem[];
  last_expiry_alert_sent_at?: string | null;
  last_expiry_alert_sent_to?: string[];
  last_expiry_alert_sent_by_name?: string | null;
  last_payment_reminder_sent_at?: string | null;
  last_payment_reminder_sent_to?: string[];
  last_payment_reminder_sent_by_name?: string | null;
};

type ReminderAction = 'expiry' | 'payment';

@Component({
  selector: 'app-subscription-detail',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, FontAwesomeModule],
  templateUrl: './subscription-detail.component.html',
  styleUrls: ['./subscription-detail.component.css']
})
export class SubscriptionDetailComponent {
  subscriptionId!: number;
  subscription: SubscriptionDetail | null = null;
  activeTab = 'overview';
  isSuperUser = false;

  payments: SubscriptionPayment[] = [];
  documents: SubscriptionDocument[] = [];

  newPayment: Partial<SubscriptionPayment> = {
    year: 1,
    amount: '0.00',
    due_date: '',
    is_paid: false,
    paid_on: ''
  };
  paymentError = '';
  docError = '';
  docTouched = false;

  docType = 'PO';
  docFile: File | null = null;
  selectedDocName = 'No file chosen';
  showUploadModal = false;
  isDragOver = false;

  reminderConfirmVisible = false;
  reminderConfirmMessage = '';
  pendingReminderAction: ReminderAction | null = null;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    library: FaIconLibrary
  ) {
    library.addIcons(
      faTrash,
      faArrowCircleLeft,
      faPen,
      faPaperPlane,
      faUpload,
      faFileUpload,
      faSquareXmark
    );
  }

  ngOnInit() {
    const user = localStorage.getItem('user');
    if (user) {
      const userObj = JSON.parse(user);
        this.isSuperUser = this.authService.isSuperUser();
    }
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.subscriptionId = Number(id);
        this.fetchSubscription();
        this.fetchPayments();
        this.fetchDocuments();
      }
    });
  }

  fetchSubscription() {
    this.apiService.getSubscription(this.subscriptionId).subscribe({
      next: (data: any) => {
        this.subscription = data as SubscriptionDetail;
      },
      error: (err) => {
        this.toastr.error(err?.error?.detail || 'Unable to load subscription.', 'Error');
      }
    });
  }

  fetchPayments() {
    this.apiService.listSubscriptionPayments(this.subscriptionId).subscribe({
      next: (data: any) => {
        this.payments = ((data as SubscriptionPayment[]) ?? []).map(payment => ({
          ...payment,
          paid_on: payment.paid_on || '',
          saving: false
        }));
      },
      error: (err) => {
        this.toastr.error(err?.error?.detail || 'Unable to load payments.', 'Error');
      }
    });
  }

  fetchDocuments() {
    this.apiService.listSubscriptionDocuments(this.subscriptionId).subscribe({
      next: (data: any) => {
        this.documents = (data as SubscriptionDocument[]) ?? [];
      },
      error: (err) => {
        this.toastr.error(err?.error?.detail || 'Unable to load documents.', 'Error');
      }
    });
  }

  sendExpiryAlert(confirmResend = false) {
    this.sendReminder('expiry', confirmResend);
  }

  sendPaymentReminder(confirmResend = false) {
    this.sendReminder('payment', confirmResend);
  }

  private sendReminder(action: ReminderAction, confirmResend = false) {
    const request$ = action === 'expiry'
      ? this.apiService.sendSubscriptionExpiryAlert(this.subscriptionId, confirmResend ? { confirm_resend: true } : {})
      : this.apiService.sendSubscriptionPaymentReminder(this.subscriptionId, confirmResend ? { confirm_resend: true } : {});

    request$.subscribe({
      next: (response: any) => {
        const detail = response?.detail || (action === 'expiry' ? 'Expiry alert sent.' : 'Payment reminder sent.');
        this.toastr.success(detail, 'Success');
        this.reminderConfirmVisible = false;
        this.pendingReminderAction = null;
        this.fetchSubscription();
      },
      error: (err) => {
        if (err?.status === 409 && err?.error?.requires_confirmation) {
          this.pendingReminderAction = action;
          this.reminderConfirmMessage = err.error.detail || 'This was already sent. Send again?';
          this.reminderConfirmVisible = true;
          return;
        }
        this.toastr.error(err?.error?.detail || 'Unable to send email.', 'Error');
      }
    });
  }

  confirmReminderResend() {
    if (!this.pendingReminderAction) {
      return;
    }
    if (this.pendingReminderAction === 'expiry') {
      this.sendExpiryAlert(true);
      return;
    }
    this.sendPaymentReminder(true);
  }

  cancelReminderConfirm() {
    this.reminderConfirmVisible = false;
    this.pendingReminderAction = null;
    this.reminderConfirmMessage = '';
  }

  savePayment() {
    this.paymentError = '';
    this.apiService.addSubscriptionPayment(this.subscriptionId, this.newPayment).subscribe({
      next: () => {
        this.newPayment = { year: 1, amount: '0.00', due_date: '', is_paid: false, paid_on: '' };
        this.fetchPayments();
        this.fetchSubscription();
        this.toastr.success('Payment added successfully.', 'Success');
      },
      error: (err) => {
        this.paymentError = err?.error?.detail || 'Unable to add payment.';
        this.toastr.error(this.paymentError, 'Error');
      }
    });
  }

  updatePaymentStatus(payment: SubscriptionPayment) {
    payment.saving = true;
    this.apiService.updateSubscriptionPayment(this.subscriptionId, {
      id: payment.id,
      is_paid: payment.is_paid,
      paid_on: payment.is_paid ? payment.paid_on || null : null
    }).subscribe({
      next: () => {
        payment.saving = false;
        this.fetchPayments();
        this.fetchSubscription();
        this.toastr.success('Payment updated successfully.', 'Success');
      },
      error: (err) => {
        payment.saving = false;
        this.toastr.error(err?.error?.detail || 'Unable to update payment.', 'Error');
      }
    });
  }

  onDocSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.docFile = input.files[0];
      this.selectedDocName = input.files[0].name;
      this.docError = '';
      this.docTouched = false;
    }
  }

  uploadDocument() {
    this.docTouched = true;
    if (!this.docFile) {
      this.docError = 'Select a document first.';
      return;
    }
    this.docError = '';
    const formData = new FormData();
    formData.append('doc_type', this.docType);
    formData.append('file', this.docFile);
    this.apiService.uploadSubscriptionDocument(this.subscriptionId, formData).subscribe({
      next: () => {
        this.docFile = null;
        this.selectedDocName = 'No file chosen';
        this.docTouched = false;
        this.showUploadModal = false;
        this.fetchDocuments();
        this.toastr.success('Document uploaded successfully.', 'Success');
      },
      error: (err) => {
        this.docError = err?.error?.detail || 'Unable to upload document.';
        this.toastr.error(this.docError, 'Error');
      }
    });
  }

  goBack() {
    this.router.navigate(['subscriptions']);
  }

  edit() {
    this.router.navigate([`subscriptions/${this.subscriptionId}/edit`]);
  }

  openUploadModal() {
    this.docError = '';
    this.docFile = null;
    this.selectedDocName = 'No file chosen';
    this.showUploadModal = true;
  }

  closeUploadModal() {
    this.showUploadModal = false;
    this.docFile = null;
    this.selectedDocName = 'No file chosen';
    this.docError = '';
  }

  clearSelectedFile() {
    this.docFile = null;
    this.selectedDocName = 'No file chosen';
    this.docError = '';
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave() {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file && file.type === 'application/pdf') {
      this.docFile = file;
      this.selectedDocName = file.name;
      this.docError = '';
    } else {
      this.docError = 'Please drop a PDF file.';
    }
  }

  getDocumentUrl(document: SubscriptionDocument): string {
    return document.file_url || document.file || '';
  }

  getDocumentName(document: SubscriptionDocument): string {
    return document.original_filename || this.getDocumentUrl(document).split('/').pop() || 'Download file';
  }

  getCameraCount(): number | string {
    if (!this.subscription) {
      return '-';
    }
    return this.subscription.cameras ?? this.subscription.camera_count ?? '-';
  }

  getLastSentTo(value?: string[] | null): string {
    return value && value.length ? value.join(', ') : '-';
  }
}
